import { useUi } from './ui';
import { useWorkspaceData } from './DemoStore';
import { useApp } from '../i18n/AppContext';
import { preferencesFor, shiftTime } from './seed';

const DEFAULT_POSTING_TIMEZONE = 'Europe/Warsaw';
const USE_WORKSPACE_TIMEZONE = 'workspace';
const CALENDAR_YEAR = 2026;
const CALENDAR_MONTH_INDEX_NOVEMBER = 10;

export function useViewTime() {
  const { route, actor } = useUi();
  const { language } = useApp();
  const { settings } = useWorkspaceData(route.workspaceId);
  const postingTimezone = settings.postingTimezone ?? DEFAULT_POSTING_TIMEZONE;
  const viewerChosenTimezone = preferencesFor(settings, actor.name).displayTimezone;
  const viewerTimezone =
    viewerChosenTimezone && viewerChosenTimezone !== USE_WORKSPACE_TIMEZONE
      ? viewerChosenTimezone
      : postingTimezone;
  const timezoneLabel = (timezone: string) => timezone.replace('_', ' ');
  const convertPostingTimeToViewerTimezone = (day: number, postingTime: string) => {
    if (viewerTimezone === postingTimezone) return { day, time: postingTime, shifted: false };
    const shiftedTime = shiftTime(postingTime, postingTimezone, viewerTimezone);
    return {
      day: day + shiftedTime.dayShift,
      time: shiftedTime.time,
      shifted: shiftedTime.dayShift !== 0,
    };
  };
  const formatCalendarDate = (day: number, useLongFormat?: boolean) =>
    new Intl.DateTimeFormat(
      language,
      useLongFormat
        ? { weekday: 'long', day: 'numeric', month: 'long' }
        : { weekday: 'short', day: 'numeric', month: 'short' }
    ).format(new Date(CALENDAR_YEAR, CALENDAR_MONTH_INDEX_NOVEMBER, day));
  const timeInViewerTimezoneWithDateIfShifted = (
    day: number,
    postingTime: string,
    useLongFormat?: boolean
  ) => {
    const viewerLocal = convertPostingTimeToViewerTimezone(day, postingTime);
    return viewerLocal.shifted
      ? `${formatCalendarDate(viewerLocal.day, useLongFormat)}, ${viewerLocal.time}`
      : viewerLocal.time;
  };
  const viewerLocalDateAndTime = (day: number, postingTime: string) => {
    const viewerLocal = convertPostingTimeToViewerTimezone(day, postingTime);
    return `${formatCalendarDate(viewerLocal.day, true)} · ${viewerLocal.time}`;
  };
  return {
    viewerTimezone: viewerTimezone,
    postingTimezone: postingTimezone,
    viewerTimezoneDiffers: viewerTimezone !== postingTimezone,
    viewerTime: timeInViewerTimezoneWithDateIfShifted,
    viewerDateTime: viewerLocalDateAndTime,
    viewerTimezoneLabel: timezoneLabel(viewerTimezone),
    postingLabel: timezoneLabel(postingTimezone),
    postingDate: (day: number) => formatCalendarDate(day, true),
    sameDay: (day: number, postingTime: string) =>
      !convertPostingTimeToViewerTimezone(day, postingTime).shifted,
  };
}
